import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { stories } from "@/mocks/stories";
import SmartImage from "@/components/base/SmartImage";

const CATEGORIES = ["All", "Field Notes", "Gear", "Materials", "Routes", "People"];

export default function JournalPage() {
  const [active, setActive] = useState("All");
  const [featured, ...rest] = stories;
  // Limit regular grid to 5 entries as per spec.
  const regular = rest.slice(0, 5);

  const filtered = useMemo(
    () => (active === "All" ? regular : regular.filter((s) => s.category === active)),
    [active, regular]
  );

  return (
    <div className="min-h-screen bg-background-50 text-foreground-100 flex flex-col">
      <SiteHeader variant="solid" />
      <main className="flex-1 pt-[72px] md:pt-20">
        {/* Compact editorial intro */}
        <section className="border-b border-white/10">
          <div className="w-full px-5 md:px-10 py-10 md:py-14 flex flex-col md:flex-row md:items-end md:justify-between gap-5 md:gap-6">
            <div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-foreground-400 mb-3">Journal</p>
              <h1 className="text-[26px] md:text-[30px] leading-[1.15] tracking-[-0.01em] text-foreground-50 font-light max-w-2xl">
                Field notes, material stories and routes worth taking.
              </h1>
            </div>
            <p className="text-[12px] text-foreground-400 md:text-right md:max-w-[220px]">
              Updated monthly. {stories.length} entries.
            </p>
          </div>

          {/* Category filter — horizontal scroll on mobile */}
          <div className="w-full px-5 md:px-10 pb-4 md:pb-6 flex items-center gap-5 md:gap-6 overflow-x-auto no-scrollbar">
            {CATEGORIES.map((c) => {
              const isActive = c === active;
              return (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  className={`nv-nowrap text-[11px] tracking-[0.18em] uppercase pb-2 cursor-pointer transition border-b min-h-[44px] flex items-center ${
                    isActive
                      ? "text-foreground-50 border-primary-500"
                      : "text-foreground-400 hover:text-foreground-200 border-transparent"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </section>

        {/* Featured story */}
        {active === "All" && (
          <section className="py-10 md:py-16 border-b border-white/10">
            <div className="w-full px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 lg:gap-12 items-center">
              <Link to={`/journal/${featured.slug}`} className="lg:col-span-8 group cursor-pointer block">
                <div className="relative w-full aspect-[3/2] overflow-hidden bg-background-900">
                  <SmartImage
                    src={featured.cover}
                    alt={featured.title}
                    tone="dark"
                    eager
                    imgClassName="block w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-[900ms] ease-out"
                  />
                </div>
              </Link>
              <div className="lg:col-span-4">
                <p className="text-[10px] tracking-[0.24em] uppercase text-primary-500 mb-3">
                  Featured · {featured.category}
                </p>
                <h2 className="text-[26px] md:text-[32px] leading-[1.12] tracking-[-0.015em] text-foreground-50 font-light mb-4">
                  {featured.title}
                </h2>
                <p className="text-[15px] md:text-[14px] text-foreground-300 leading-relaxed mb-5 font-light">{featured.excerpt}</p>
                <p className="text-[11px] tracking-[0.15em] uppercase text-foreground-400 mb-6">{featured.meta}</p>
                <Link
                  to={`/journal/${featured.slug}`}
                  className="text-[12px] tracking-[0.15em] uppercase text-foreground-50 hover:text-primary-400 border-b border-foreground-300/60 hover:border-primary-400 pb-1 cursor-pointer inline-flex items-center gap-2"
                >
                  Read story <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Editorial 3-col grid, uniform 3:2 landscape imagery, no cards */}
        <section className="py-12 md:py-20">
          <div className="w-full px-5 md:px-10">
            {filtered.length === 0 ? (
              <p className="text-[13px] text-foreground-400 py-10">No entries in this category yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-7 md:gap-x-8 gap-y-10 md:gap-y-14">
                {filtered.map((s) => (
                  <Link key={s.slug} to={`/journal/${s.slug}`} className="group cursor-pointer">
                    <div className="relative w-full aspect-[3/2] overflow-hidden bg-background-900 mb-5">
                      <SmartImage
                        src={s.cover}
                        alt={s.title}
                        tone="dark"
                        imgClassName="block w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-[900ms] ease-out"
                      />
                    </div>
                    <p className="text-[10px] tracking-[0.24em] uppercase text-primary-500 mb-2">{s.category}</p>
                    <h3 className="text-[18px] md:text-[19px] leading-[1.2] tracking-[-0.01em] text-foreground-50 font-light group-hover:text-primary-400 transition mb-2">
                      {s.title}
                    </h3>
                    <p className="text-[11px] tracking-[0.14em] uppercase text-foreground-400">{s.meta}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}